import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OfflineIndicator from './OfflineIndicator';
import AbonelikUyariBanner from './AbonelikUyariBanner';
import {
  canAccessUsers,
  canAccessWorkOrders,
  canAccessCustomers,
  canAccessElevators,
  canAccessDashboard,
  getRoleDisplayName
} from '../utils/permissions';
import {
  LayoutDashboard, Wrench, Building2, Users, Package, FileText, BarChart3,
  Settings, LogOut, Menu, X, QrCode, User, Calendar, DollarSign,
  ChevronDown, Search, Calculator, ClipboardList, Key, Cpu,
  Link2, Brain, Activity, Eye, Sun, Moon, MoreHorizontal, Bell, CreditCard, ShieldCheck,
  ClipboardCheck, CalendarCheck, FileSignature, Upload, SlidersHorizontal, BellRing, HardHat,
  Fingerprint
} from 'lucide-react';
import useMobilTablo from './ds/mobilTablo';

const initialsOf = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'M';

const Layout = ({ children }) => {
  // Eski ekranlardaki tablolari dar ekranda karta cevirir; etiketler
  // baslik satirindan calisma aninda aliniyor (bkz. ds/mobilTablo).
  useMobilTablo();

  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  // tenant: cok kiracili kurulumda giris yapilan firma (AuthContext saglar).
  // Eski kurulumlarda bu deger yok; o durumda marka blogu eski gorunumune duser.
  const { user, logout, tenant } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const toggleTheme = () => {
    const isDark = !dark;
    setDark(isDark);
    document.documentElement.classList.toggle('dark', isDark);
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  };

  useEffect(() => {
    if (localStorage.getItem('theme') === 'dark') {
      document.documentElement.classList.add('dark');
      setDark(true);
    }
  }, []);

  // Close mobile drawer on route change
  useEffect(() => { setSidebarOpen(false); setUserMenuOpen(false); }, [location.pathname]);

  const dashboardItem = {
    name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard,
    canAccess: () => canAccessDashboard(user)
  };

  /**
   * Menu.
   *
   * Onceden 31 oge, bes kategoride ve icinde cakisan ekranlar vardi:
   * sozlesme uc yerde, montaj iki yerde, uyari uc yerde. Kullanici hangi
   * ekrana gidecegini bilemiyordu.
   *
   * Cakisan ekranlar sekmeli tek sayfada birlestirildi (bkz. pages/Montaj,
   * Uyarilar, Envanter, YapayZeka ve Ticari). Kategoriler artik ISE gore:
   *
   *   Operasyon : gunluk saha ve bakim isi
   *   Ticari    : para ve sozlesme
   *   Yonetim   : ana veri (musteri, personel)
   *   Ayarlar   : sistemi yapilandirma
   *
   * Sik kullanilan "Islerim" ve "Uyarilar" kategori icinde degil ustte:
   * teknisyen gunde on kez oraya giriyor.
   */
  const navigationCategories = [
    {
      id: 'operations', name: t('nav.categories.operations'), icon: Wrench,
      items: [
        { name: 'İşlerim (Saha)', href: '/saha', icon: HardHat, canAccess: () => canAccessWorkOrders(user) },
        { name: 'Uyarılar', href: '/uyarilar', icon: BellRing, canAccess: () => canAccessWorkOrders(user) },
        { name: t('nav.workOrders'), href: '/work-orders', icon: Wrench, canAccess: () => canAccessWorkOrders(user) },
        { name: t('nav.qrScanner'), href: '/qr-scanner', icon: QrCode, canAccess: () => canAccessWorkOrders(user) },
        { name: t('nav.elevators'), href: '/elevators', icon: Building2, canAccess: () => canAccessElevators(user) },
        { name: 'Mevzuat / Uyum', href: '/uyum', icon: ShieldCheck, canAccess: () => canAccessElevators(user) },
        { name: 'Bakım Operasyon', href: '/bakim-operasyon', icon: CalendarCheck, canAccess: () => canAccessWorkOrders(user) },
        { name: 'Montaj', href: '/montaj', icon: ClipboardCheck, canAccess: () => canAccessElevators(user) },
        { name: 'Operasyon Merkezi', href: '/domain-operations', icon: ClipboardList, canAccess: () => canAccessWorkOrders(user) },
        { name: 'Supervisor Planlama', href: '/supervisor-planning', icon: Calendar, canAccess: () => user?.role === 'supervisor' || user?.role === 'yonetici' }
      ]
    },
    {
      id: 'finance', name: t('nav.categories.finance'), icon: DollarSign,
      items: [
        { name: 'Sözleşme ve Tahsilat', href: '/ticari', icon: FileSignature, canAccess: () => canAccessWorkOrders(user) },
        { name: t('nav.invoices'), href: '/invoices', icon: FileText, canAccess: () => canAccessWorkOrders(user) },
        { name: 'Asansör Fiyat Teklifi', href: '/price-quote', icon: Calculator, canAccess: () => canAccessWorkOrders(user) },
        { name: 'Envanter ve Parça Fiyatları', href: '/envanter', icon: Package, canAccess: () => canAccessWorkOrders(user) }
      ]
    },
    {
      id: 'management', name: t('nav.categories.management'), icon: Users,
      items: [
        { name: t('nav.customers'), href: '/customers', icon: Users, canAccess: () => canAccessCustomers(user) },
        { name: 'İnsan Kaynakları', href: '/personnel-management', icon: Users, canAccess: () => canAccessUsers(user) },
        { name: 'Yapay Zekâ', href: '/yapay-zeka', icon: Brain, canAccess: () => !!user },
        { name: t('nav.chat'), href: '/chat', icon: User, canAccess: () => !!user }
      ]
    },
    {
      id: 'settings', name: 'Ayarlar', icon: Settings,
      items: [
        { name: 'Parametreler', href: '/parametreler', icon: SlidersHorizontal, canAccess: () => user?.role === 'yonetici' },
        { name: 'Kullanıcı Hesapları', href: '/users', icon: Users, canAccess: () => canAccessUsers(user) },
        { name: 'Veri Aktarımı', href: '/kurulum-aktarimi', icon: Upload, canAccess: () => user?.role === 'yonetici' },
        { name: 'Entegrasyonlar', href: '/entegrasyonlar', icon: Key, canAccess: () => user?.role === 'yonetici' },
        { name: 'Abonelik ve Kullanım', href: '/subscription', icon: CreditCard, canAccess: () => user?.role === 'yonetici' },
        { name: 'KVKK', href: '/kvkk', icon: Fingerprint, canAccess: () => user?.role === 'yonetici' },
        { name: 'Denetim Zinciri', href: '/integrity-chain', icon: Activity, canAccess: () => user?.role === 'yonetici' }
      ]
    }
  ];

  const [openCategories, setOpenCategories] = useState(new Set());
  const currentPath = location.pathname;

  const getCurrentCategory = () => {
    if (currentPath === '/dashboard') return null;
    for (const c of navigationCategories) {
      if (c.items.some((i) => currentPath.startsWith(i.href))) return c.id;
    }
    return null;
  };

  useEffect(() => {
    const cur = getCurrentCategory();
    if (cur) setOpenCategories((prev) => new Set([...prev, cur]));
  }, [currentPath]);

  const toggleCategory = (id) =>
    setOpenCategories((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });

  const filteredCategories = navigationCategories.filter((c) => c.items.some((i) => i.canAccess()));
  const isActive = (href) => currentPath === href || currentPath.startsWith(href + '/');

  const handleLogout = async () => { await logout(); navigate('/'); };

  // Topbar araması
  //
  // Bu kutu daha once yalnizca gorseldi; hicbir olay isleyicisi yoktu, yazi
  // yazilip Enter'a basildiginda hicbir sey olmuyordu.
  //
  // Girdinin sekline gore en olasi hedefe yonlendiriyoruz; her sayfa kendi
  // ?search= parametresini zaten destekliyor.
  const [searchTerm, setSearchTerm] = useState('');

  const searchTargetFor = (raw) => {
    const q = raw.trim();
    if (!q) return null;
    // WO-2026-0042 gibi is emri numarasi
    if (/^(wo|is)[-_]?\d/i.test(q)) return `/work-orders?search=${encodeURIComponent(q)}`;
    // INV-2026-003 gibi fatura numarasi
    if (/^inv[-_]?\d/i.test(q)) return `/invoices?search=${encodeURIComponent(q)}`;
    // ASN-001 / XYZ-001 gibi asansor kodu
    if (/^[a-z]{2,4}[-_]\d+$/i.test(q)) return `/elevators?search=${encodeURIComponent(q)}`;
    // Aksi halde musteri araması
    return `/customers?search=${encodeURIComponent(q)}`;
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    const target = searchTargetFor(searchTerm);
    if (!target) return;
    navigate(target);
    setSearchTerm('');
  };

  // Brand lockup ----------------------------------------------------------
  const Brand = ({ compact }) => (
    <Link to="/dashboard" style={{ display: 'flex', alignItems: 'center', gap: 11, textDecoration: 'none' }}>
      <div style={{
        width: 38, height: 38, borderRadius: 11, flexShrink: 0,
        background: 'linear-gradient(140deg, #6366F1, #4338CA)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 6px 16px -4px rgba(79,70,229,0.55)'
      }}>
        <Building2 style={{ width: 21, height: 21, color: '#fff' }} strokeWidth={2.4} />
      </div>
      {!compact && (
        <div style={{ lineHeight: 1.1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
            Maintenance<span style={{ color: '#818CF8' }}>360</span>
          </div>
          {/* Alt satirda urun degil FIRMA adi: ayni tarayicida birden fazla
              firmaya giren kullanici hangi hesapta oldugunu gormeli.
              Kiraci bilgisi yoksa eski alt baslik korunur. */}
          <div
            title={tenant?.name || 'Asansör CMMS'}
            style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
              color: 'var(--shell-text-muted)', textTransform: 'uppercase',
              maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {tenant?.name || 'Asansör CMMS'}
          </div>
        </div>
      )}
    </Link>
  );

  // Shared nav list (used in desktop sidebar + mobile drawer) -------------
  const NavList = () => (
    <nav style={{ padding: '12px 12px 20px', flex: 1, overflowY: 'auto' }}>
      {dashboardItem.canAccess() && (
        <Link to={dashboardItem.href} className="nav-item" data-active={isActive('/dashboard')}>
          <dashboardItem.icon style={{ width: 18, height: 18, flexShrink: 0 }} />
          <span>{dashboardItem.name}</span>
        </Link>
      )}

      {filteredCategories.map((category) => {
        const isOpen = openCategories.has(category.id);
        const items = category.items.filter((i) => i.canAccess());
        if (!items.length) return null;
        return (
          <div key={category.id} style={{ marginTop: 6 }}>
            <button onClick={() => toggleCategory(category.id)} className="nav-cat">
              <category.icon style={{ width: 16, height: 16, flexShrink: 0, color: 'var(--shell-text-muted)' }} />
              <span style={{ flex: 1, textAlign: 'left' }}>{category.name}</span>
              <span className="nav-cat-count">{items.length}</span>
              <ChevronDown style={{ width: 14, height: 14, flexShrink: 0, transition: 'transform .2s ease', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
            </button>
            {isOpen && (
              <div style={{ marginTop: 2 }}>
                {items.map((item) => (
                  <Link key={item.name} to={item.href} className="nav-item nav-sub" data-active={isActive(item.href)}>
                    <item.icon style={{ width: 16, height: 16, flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  const navStyles = `
    .nav-item {
      display: flex; align-items: center; gap: 11px;
      padding: 9px 12px; margin: 1px 0; border-radius: 9px;
      font-size: 13.5px; font-weight: 600; text-decoration: none;
      color: var(--shell-text); position: relative;
      transition: background-color .18s ease, color .18s ease;
    }
    .nav-item:hover { background: var(--shell-item-hover); color: #fff; }
    .nav-item[data-active="true"] { background: var(--shell-active-bg); color: #fff; }
    .nav-item[data-active="true"]::before {
      content: ""; position: absolute; left: -12px; top: 50%; transform: translateY(-50%);
      width: 3px; height: 20px; border-radius: 0 3px 3px 0; background: var(--shell-accent);
    }
    .nav-item[data-active="true"] svg { color: var(--shell-accent); }
    .nav-sub { font-weight: 500; font-size: 13px; padding-left: 14px; }
    .nav-cat {
      width: 100%; display: flex; align-items: center; gap: 11px;
      padding: 9px 12px; border: none; background: transparent; cursor: pointer;
      border-radius: 9px; color: var(--shell-text);
      font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
      transition: background-color .18s ease;
    }
    .nav-cat:hover { background: var(--shell-item-hover); }
    .nav-cat-count {
      font-size: 10.5px; font-weight: 700; color: var(--shell-text-muted);
      background: var(--shell-item-hover); border-radius: 999px; padding: 1px 7px; min-width: 20px; text-align: center;
    }
    .topbar-icon-btn {
      width: 40px; height: 40px; border-radius: 10px; border: 1px solid var(--border);
      background: var(--surface); color: var(--text-muted); cursor: pointer;
      display: flex; align-items: center; justify-content: center; position: relative;
      transition: background-color .18s ease, color .18s ease, border-color .18s ease;
    }
    .topbar-icon-btn:hover { background: var(--surface-hover); color: var(--text-strong); border-color: var(--border-strong); }
    .bottom-tab { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px;
      padding: 7px 0; background: none; border: none; cursor: pointer;
      font-size: 10.5px; font-weight: 600; color: var(--text-faint); text-decoration: none; }
    .bottom-tab[data-active="true"] { color: var(--primary); }
  `;

  return (
    <>
      <style>{navStyles}</style>
      <div style={{ minHeight: '100vh', background: 'var(--bg-app)' }}>
        <OfflineIndicator />

        {/* ===== Mobile drawer overlay ===== */}
        {sidebarOpen && (
          <div className="lg:hidden" onClick={() => setSidebarOpen(false)} aria-hidden="true"
            style={{ position: 'fixed', inset: 0, background: 'rgba(8,12,22,0.55)', backdropFilter: 'blur(2px)', zIndex: 999 }} />
        )}

        {/* ===== Mobile drawer ===== */}
        <aside className="lg:hidden" style={{
          position: 'fixed', top: 0, left: sidebarOpen ? 0 : '-300px', width: 284, height: '100vh',
          background: 'var(--shell-bg)', zIndex: 1000, transition: 'left .28s cubic-bezier(0.16,1,0.3,1)',
          display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--shell-border)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 16px', borderBottom: '1px solid var(--shell-border)' }}>
            <Brand />
            <button onClick={() => setSidebarOpen(false)} style={{ background: 'var(--shell-item-hover)', border: 'none', borderRadius: 9, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <X style={{ width: 18, height: 18, color: 'var(--shell-text)' }} />
            </button>
          </div>
          <NavList />
          <div style={{ borderTop: '1px solid var(--shell-border)', padding: 12 }}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {['tr', 'en'].map((lng) => (
                <button key={lng} onClick={() => { i18n.changeLanguage(lng); localStorage.setItem('lang', lng); }}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    background: i18n.language === lng ? 'var(--shell-active-bg)' : 'var(--shell-item-hover)',
                    color: i18n.language === lng ? '#fff' : 'var(--shell-text-muted)' }}>
                  {lng === 'tr' ? 'Türkçe' : 'English'}
                </button>
              ))}
            </div>
            <button onClick={handleLogout} style={{ width: '100%', padding: '10px', background: 'rgba(225,29,72,0.12)', color: '#FB7185', border: 'none', borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 700 }}>
              <LogOut style={{ width: 15, height: 15 }} /> Çıkış Yap
            </button>
          </div>
        </aside>

        {/* ===== Desktop sidebar ===== */}
        <aside className="desktop-only" style={{
          position: 'fixed', top: 0, left: 0, width: 260, height: '100vh', zIndex: 30,
          background: 'var(--shell-bg)', borderRight: '1px solid var(--shell-border)',
          display: 'flex', flexDirection: 'column'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', height: 68, padding: '0 18px', borderBottom: '1px solid var(--shell-border)' }}>
            <Brand />
          </div>
          <NavList />
          <div style={{ borderTop: '1px solid var(--shell-border)', padding: 12 }}>
            <button onClick={() => navigate('/user-settings')} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: 8, background: 'var(--shell-item-hover)', border: 'none', borderRadius: 11, cursor: 'pointer', textAlign: 'left' }}>
              <div className="ds-avatar" style={{ width: 36, height: 36, fontSize: 13, flexShrink: 0 }}>{initialsOf(user?.full_name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.full_name || 'Kullanıcı'}</div>
                <div style={{ fontSize: 11, color: 'var(--shell-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getRoleDisplayName(user?.role)}</div>
              </div>
              <LogOut onClick={(e) => { e.stopPropagation(); handleLogout(); }} style={{ width: 16, height: 16, color: 'var(--shell-text-muted)', flexShrink: 0 }} />
            </button>
          </div>
        </aside>

        {/* ===== Desktop topbar ===== */}
        <header className="desktop-only desktop-topbar" style={{
          position: 'sticky', top: 0, zIndex: 20, marginLeft: 260,
          background: 'var(--surface)', borderBottom: '1px solid var(--border)',
          height: 68, padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16
        }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
            <Search style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', width: 17, height: 17, color: 'var(--text-faint)' }} />
            <input
              placeholder="Ara: iş emri, asansör, müşteri…"
              className="ds-input"
              style={{ paddingLeft: 38, height: 42, background: 'var(--surface-3)' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Genel arama"
            />
          </div>
          <div style={{ flex: 1 }} />
          <button className="topbar-icon-btn" onClick={toggleTheme} aria-label="Tema">
            {dark ? <Sun style={{ width: 18, height: 18 }} /> : <Moon style={{ width: 18, height: 18 }} />}
          </button>
          <div style={{ position: 'relative' }}>
            <button onClick={() => setUserMenuOpen((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 9px 5px 5px', background: 'var(--surface-3)', border: '1px solid var(--border)', borderRadius: 999, cursor: 'pointer' }}>
              <div className="ds-avatar" style={{ width: 32, height: 32, fontSize: 12 }}>{initialsOf(user?.full_name)}</div>
              <div style={{ textAlign: 'left', lineHeight: 1.2 }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{user?.full_name?.split(' ')[0]}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{getRoleDisplayName(user?.role)}</div>
              </div>
              <ChevronDown style={{ width: 15, height: 15, color: 'var(--text-faint)' }} />
            </button>
            {userMenuOpen && (
              <div style={{ position: 'absolute', right: 0, top: 50, width: 200, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-lg)', padding: 6, zIndex: 50 }}>
                {[{ label: 'Profilim', icon: User, to: '/profile' }, { label: 'Ayarlar', icon: Settings, to: '/user-settings' }].map((m) => (
                  <button key={m.to} onClick={() => { navigate(m.to); setUserMenuOpen(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                    <m.icon style={{ width: 16, height: 16, color: 'var(--text-muted)' }} /> {m.label}
                  </button>
                ))}
                <div className="ds-divider" style={{ margin: '6px 0' }} />
                <button onClick={handleLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--danger-text)' }}>
                  <LogOut style={{ width: 16, height: 16 }} /> Çıkış Yap
                </button>
              </div>
            )}
          </div>
        </header>

        {/* ===== Mobile topbar ===== */}
        <header className="mobile-only" style={{
          position: 'sticky', top: 0, zIndex: 20, height: 60, padding: '0 12px',
          background: 'var(--surface)', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <button onClick={() => setSidebarOpen(true)} aria-label="Menü" style={{ width: 40, height: 40, borderRadius: 10, border: 'none', background: 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Menu style={{ width: 20, height: 20, color: 'var(--text-strong)' }} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(140deg, #6366F1, #4338CA)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Building2 style={{ width: 16, height: 16, color: '#fff' }} strokeWidth={2.4} />
            </div>
            <div style={{ lineHeight: 1.05, minWidth: 0 }}>
              <div style={{ fontSize: 15.5, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-0.02em' }}>
                Maintenance<span style={{ color: 'var(--primary)' }}>360</span>
              </div>
              {tenant?.name && (
                <div
                  title={tenant.name}
                  style={{
                    fontSize: 10, fontWeight: 700, color: 'var(--text-faint)',
                    maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {tenant.name}
                </div>
              )}
            </div>
          </div>
          <div style={{ width: 40, height: 40 }} />
        </header>

        {/* ===== Page content =====
            Tek main: mobil ile masaustu arasindaki fark yalnizca yerlesim,
            bu yuzden .app-main ile CSS'te cozuluyor. Iki ayri main
            kullanilirsa her sayfa bileseni iki kez mount olur. */}
        <main className="app-main">
          {/* Abonelik uyarisi icerigin EN USTUNDE: kullanici sayfaya
              girdiginde once neden kisitli oldugunu gormeli. Gosterilecek
              bir sey yoksa bilesen null doner. Sarmal, seridin altindaki
              sayfa govdesiyle (ds-page) ayni genislikte hizalanmasi icin. */}
          <div className="ds-page">
            <AbonelikUyariBanner />
          </div>
          {children}
        </main>

        {/* ===== Mobile bottom tab bar ===== */}
        <nav className="mobile-only" style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, height: 64, zIndex: 40,
          background: 'var(--surface)', borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'stretch', paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}>
          {[
            { label: 'Panel', icon: LayoutDashboard, to: '/dashboard' },
            { label: 'Saha', icon: HardHat, to: '/saha' },
            { label: 'QR', icon: QrCode, to: '/qr-scanner', fab: true },
            { label: 'Asansör', icon: Building2, to: '/elevators' },
            { label: 'Menü', icon: MoreHorizontal, action: () => setSidebarOpen(true) }
          ].map((tab) =>
            tab.fab ? (
              <button key={tab.label} onClick={() => navigate(tab.to)} className="bottom-tab" style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', top: -18, width: 50, height: 50, borderRadius: 16, background: 'linear-gradient(140deg, #6366F1, #4338CA)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: 'var(--shadow-primary)' }}>
                  <tab.icon style={{ width: 22, height: 22, color: '#fff' }} />
                </div>
                <span style={{ marginTop: 34 }}>{tab.label}</span>
              </button>
            ) : tab.action ? (
              <button key={tab.label} onClick={tab.action} className="bottom-tab">
                <tab.icon style={{ width: 21, height: 21 }} />
                <span>{tab.label}</span>
              </button>
            ) : (
              <Link key={tab.label} to={tab.to} className="bottom-tab" data-active={isActive(tab.to)}>
                <tab.icon style={{ width: 21, height: 21 }} />
                <span>{tab.label}</span>
              </Link>
            )
          )}
        </nav>
      </div>
    </>
  );
};

export default Layout;
